<app-card-widget title="Finances" *ngIf="invoice$ | async">

  <ng-container *ngIf="invoice$ | async as data">
    <form [formGroup]="form" id="invoice-financing-v2-form" (ngSubmit)="save()" *ngIf="maps$ | async as maps" novalidate>

      <div *ngIf="reloadMessage" class="banner banner-warning">
        <span class="banner-text">{{ reloadMessage }}</span>
        <button type="button" mat-button (click)="reload()">Reload</button>
      </div>
      <div *ngIf="stateLockedMessage" class="banner banner-error">
        <span class="banner-text">{{ stateLockedMessage }}</span>
      </div>
      <div *ngIf="financesInvalidSummary" class="banner banner-error">
        <div class="banner-text">
          <div>{{ financesInvalidSummary }}</div>
          <ul *ngIf="financesInvalidDetails.length > 0" class="error-list">
            <li *ngFor="let detail of financesInvalidDetails">
              <strong>{{ detail.field }}</strong>: {{ detail.message }}
            </li>
          </ul>
        </div>
      </div>

      <div class="card-section-container">
        <div class="card-section">

          <app-text label="Configuration" *ngIf="!edited">{{ maps.configurations.get(data.configuration_id)?.name || placeholder }}</app-text>

          <mat-form-field *ngIf="edited">
            <mat-select placeholder="Configuration" formControlName="configuration_id">
              <mat-option *ngFor="let option of maps.configurations.all()" [value]="option.invoiceConfigurationId">
                {{ option.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <app-text label="Financing Method" *ngIf="!edited">{{ maps.financingMethods.get(data.financing_method_id)?.name || placeholder }}</app-text>

          <mat-form-field *ngIf="edited">
            <mat-select placeholder="Financing Method" formControlName="financing_method_id">
              <mat-option *ngFor="let option of maps.financingMethods.all()" [value]="option.financingMethodId">
                {{ option.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <app-text label="Daily Interest (%)" *ngIf="!edited && data.financing_method_id === FinancingMethodId.DailyInterest">{{ data.daily_interest || placeholder }}</app-text>

          <mat-form-field *ngIf="edited && form.value.financing_method_id === FinancingMethodId.DailyInterest" [class.field-error]="hasFieldError('daily_interest')">
            <input matInput placeholder="Daily Interest (%)" type="number" formControlName="daily_interest" step="0.01" />
            <mat-hint *ngIf="hasFieldError('daily_interest')" class="error-hint">{{ getFieldError('daily_interest') }}</mat-hint>
          </mat-form-field>

          <app-text label="Fixed Interest (%)" *ngIf="!edited && data.financing_method_id === FinancingMethodId.FixedInterest">{{ data.fixed_interest || placeholder }}</app-text>

          <mat-form-field *ngIf="edited && form.value.financing_method_id === FinancingMethodId.FixedInterest" [class.field-error]="hasFieldError('fixed_interest')">
            <input matInput placeholder="Fixed Interest (%)" type="number" formControlName="fixed_interest" step="0.01" />
            <mat-hint *ngIf="hasFieldError('fixed_interest')" class="error-hint">{{ getFieldError('fixed_interest') }}</mat-hint>
          </mat-form-field>

          <app-text label="Penalty Interest Method" *ngIf="!edited">{{ maps.penaltyInterestMethods.get(data.penalty_interest_method_id)?.name || placeholder }}</app-text>

          <mat-form-field *ngIf="edited">
            <mat-select placeholder="Penalty Interest Method" formControlName="penalty_interest_method_id">
              <mat-option [value]="null"></mat-option>
              <mat-option *ngFor="let option of maps.penaltyInterestMethods.all()" [value]="option.penaltyInterestMethodId">
                {{ option.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <app-text label="Guarantee (%)" *ngIf="!edited">{{ data.guarantee || placeholder }}</app-text>

          <mat-form-field *ngIf="edited" [class.field-error]="hasFieldError('guarantee')">
            <input matInput placeholder="Guarantee (%)" type="number" formControlName="guarantee" />
            <mat-hint *ngIf="hasFieldError('guarantee')" class="error-hint">{{ getFieldError('guarantee') }}</mat-hint>
          </mat-form-field>

        </div>
        <div class="card-section">

          <app-text label="Financed Days">{{ data.financed_days ?? placeholder }}</app-text>
          <app-text label="Express Payment Fee">{{ data.express_payment_fee ? (data.express_payment_fee | money) : placeholder }}</app-text>

        </div>
        <div class="card-section">

          <mat-form-field *ngIf="edited" [class.field-error]="hasFieldError('service_fee')">
            <input matInput placeholder="Service Fee (€)" type="number" formControlName="service_fee" />
            <span matSuffix *ngIf="data.service_fee_vat">(incl. {{ data.service_fee_vat }}% VAT)</span>
            <mat-hint *ngIf="hasFieldError('service_fee')" class="error-hint">{{ getFieldError('service_fee') }}</mat-hint>
          </mat-form-field>
          <app-text label="Service Fee" *ngIf="!edited">
            {{ data.service_fee ? (data.service_fee | money) : placeholder }}
            <span *ngIf="data.service_fee_vat"> (incl. {{ data.service_fee_vat }}% VAT)</span>
          </app-text>

          <app-text label="Financing Fee">{{ data.financing_fee ? (data.financing_fee | money) : placeholder }}</app-text>
          <app-text label="Penalty Interest Fee">{{ data.penalty_interest_fee ? (data.penalty_interest_fee | money) : placeholder }}</app-text>

          <mat-form-field *ngIf="edited" [class.field-error]="hasFieldError('subfinancing_cost')">
            <input matInput placeholder="Subfinancing Cost (€)" type="number" formControlName="subfinancing_cost" />
            <mat-hint *ngIf="hasFieldError('subfinancing_cost')" class="error-hint">{{ getFieldError('subfinancing_cost') }}</mat-hint>
          </mat-form-field>
          <app-text label="Subfinancing Cost" *ngIf="!edited">{{ data.subfinancing_cost ? (data.subfinancing_cost | money) : placeholder }}</app-text>

          <app-text label="Total Profit">{{ data.total_profit ? (data.total_profit | money) : placeholder }}</app-text>

        </div>
      </div>

      <div style="height: 32px"></div>

      <div class="card-section-container">
        <div class="card-section">
          <app-text label="Due Date">{{ data.due_date ? (data.due_date | date:'shortDate') : placeholder }}</app-text>
        </div>
        <div class="card-section">
          <app-text label="Interest End Date" *ngIf="!edited">
            {{ data.effective_interest_end_date ? (data.effective_interest_end_date | date:'shortDate') : placeholder }}
            <span *ngIf="data.interest_end_date_override"> (override)</span>
          </app-text>

          <!--
            The three date pickers (this one, Financed Date and Final Payment
            Date) refuse unreadable text and a typed day before 1.1.2000, or
            before the field's stored day when that is earlier, and say why
            once touched. The server's message for the field shows instead
            until the handler edits it, which clears it, so the two never show
            at once. The clear buttons still empty a field, and Save sends null.
          -->
          <mat-form-field *ngIf="edited" [class.field-error]="hasFieldError('interest_end_date_override')">
            <input matInput [matDatepicker]="interestEndDateOverridePicker" [min]="dateFields.earliest('interest_end_date_override')" placeholder="Interest End Date Override" formControlName="interest_end_date_override" />
            <mat-datepicker-toggle matSuffix [for]="interestEndDateOverridePicker"></mat-datepicker-toggle>
            <mat-datepicker #interestEndDateOverridePicker></mat-datepicker>
            <button type="button" mat-icon-button matSuffix *ngIf="form.get('interest_end_date_override')?.value" (click)="clearInterestEndDateOverride()" aria-label="Clear override">
              <mat-icon>clear</mat-icon>
            </button>
            <mat-hint *ngIf="!hasFieldError('interest_end_date_override')">Leave empty to use due date / actual payment date.</mat-hint>
            <mat-hint *ngIf="hasFieldError('interest_end_date_override')" class="error-hint">{{ getFieldError('interest_end_date_override') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('interest_end_date_override')" class="error-hint">{{ dateFields.message('interest_end_date_override') }}</mat-error>
          </mat-form-field>
        </div>
        <div class="card-section"></div>
      </div>

      <div style="height: 32px"></div>

      <div class="card-section-container">
        <div class="card-section">

          <app-text label="Financed Date" *ngIf="!edited">{{ data.financed_date ? (data.financed_date | date:'shortDate') : placeholder }}</app-text>

          <mat-form-field *ngIf="edited" [class.field-error]="hasFieldError('financed_date')">
            <input matInput [matDatepicker]="financedDatePicker" [min]="dateFields.earliest('financed_date')" placeholder="Financed Date" formControlName="financed_date" />
            <mat-datepicker-toggle matSuffix [for]="financedDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #financedDatePicker></mat-datepicker>
            <button type="button" mat-icon-button matSuffix *ngIf="form.get('financed_date')?.value" (click)="clearFinancedDate()" aria-label="Clear financed date">
              <mat-icon>clear</mat-icon>
            </button>
            <mat-hint *ngIf="hasFieldError('financed_date')" class="error-hint">{{ getFieldError('financed_date') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('financed_date')" class="error-hint">{{ dateFields.message('financed_date') }}</mat-error>
          </mat-form-field>

          <app-text label="Financed Amount">{{ data.amount_financed ? (data.amount_financed | money) : placeholder }}</app-text>

        </div>
        <div class="card-section">
          <app-text label="Final Payment Date" *ngIf="!edited || !isFinalPaymentDateEditable(data)">{{ data.final_payment_date ? (data.final_payment_date | date:'shortDate') : placeholder }}</app-text>

          <mat-form-field *ngIf="edited && isFinalPaymentDateEditable(data)" [class.field-error]="hasFieldError('final_payment_date')">
            <input matInput [matDatepicker]="finalPaymentDatePicker" [min]="dateFields.earliest('final_payment_date')" placeholder="Final Payment Date" formControlName="final_payment_date" />
            <mat-datepicker-toggle matSuffix [for]="finalPaymentDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #finalPaymentDatePicker></mat-datepicker>
            <button type="button" mat-icon-button matSuffix *ngIf="form.get('final_payment_date')?.value" (click)="clearFinalPaymentDate()" aria-label="Clear final payment date">
              <mat-icon>clear</mat-icon>
            </button>
            <mat-hint *ngIf="hasFieldError('final_payment_date')" class="error-hint">{{ getFieldError('final_payment_date') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('final_payment_date')" class="error-hint">{{ dateFields.message('final_payment_date') }}</mat-error>
          </mat-form-field>

          <app-text label="Final Payment Amount">{{ data.final_payment_amount ? (data.final_payment_amount | money) : placeholder }}</app-text>
        </div>
        <div class="card-section">
          <app-text label="Last Payment Date">{{ data.last_payment_date ? (data.last_payment_date | date:'shortDate') : placeholder }}</app-text>
          <app-text label="Total Paid Amount">{{ data.total_paid_amount ? (data.total_paid_amount | money) : placeholder }}</app-text>
        </div>
      </div>

    </form>

    <mat-card-actions align="end">
      <!--
        Edit needs invoice:edit-finances on top of the page lock; the
        permission narrows the gate, never replaces it. Without it the button
        stays visible but disabled. A disabled button gets no pointer events
        and takes no focus, so the tooltip saying why sits on this wrapper.
        Only once the permission is known to be missing is the tooltip enabled
        and the wrapper a named tab stop, so a keyboard user gets the reason
        too: the tooltip opens on keyboard focus and describes the wrapper.
        Until then its message is empty, since MatTooltip describes its host
        even while disabled, and a permitted user's screen reader would
        otherwise hear the refusal next to an enabled Edit.
        The ngIf holds one object so every binding reads one subscription,
        and `=== false` / `!== true` are deliberate: the permission is
        unknown, not missing, until the current user loads.
      -->
      <ng-container *ngIf="{ allowed: canEditFinances$ | async } as editGate">
        <span
          *ngIf="!edited"
          class="edit-action"
          [matTooltip]="editGate.allowed === false ? editPermissionTooltip : ''"
          [matTooltipDisabled]="editGate.allowed !== false"
          [attr.tabindex]="editGate.allowed === false ? 0 : null"
          [attr.role]="editGate.allowed === false ? 'group' : null"
          [attr.aria-label]="editGate.allowed === false ? 'Edit' : null"
        >
          <button type="button" mat-button [disabled]="locked || editGate.allowed !== true" (click)="edit()">Edit</button>
        </span>
      </ng-container>
      <button type="submit" mat-button *ngIf="edited" [disabled]="!form.valid || saving" form="invoice-financing-v2-form">Save</button>
      <button type="reset" mat-button *ngIf="edited" (click)="revert()">Cancel</button>
    </mat-card-actions>
  </ng-container>

</app-card-widget>
